/**
 * Routing-specific sanitization utilities
 * Used for URL/path sanitization in route guards
 * 
 * SECURITY: These functions prevent open redirect attacks and path traversal
 */

/**
 * Sanitize a path string for safe use in routing
 * Removes dangerous characters, query params, hash fragments, and limits length
 * 
 * @param input - The path string to sanitize
 * @param maxLength - Maximum allowed length (default: 500)
 * @returns Sanitized path string, or empty string if invalid
 */
export function sanitizePath(input: string, maxLength: number = 500): string {
  if (typeof input !== 'string') return '';
  
  // SECURITY: Remove query parameters and hash fragments (prevent injection via query/hash)
  let sanitized = input.split('?')[0].split('#')[0];
  
  // SECURITY: Remove dangerous characters (HTML/script injection prevention)
  sanitized = sanitized.replace(/[<>"'&]/g, '');
  
  // SECURITY: Remove protocol schemes (prevent javascript:, data:, vbscript:)
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  
  // SECURITY: Limit length and trim
  return sanitized.substring(0, maxLength).trim();
}

/**
 * Validate if a path is safe for internal routing
 * Prevents open redirect attacks, path traversal, and injection attacks
 * 
 * @param path - The path to validate
 * @returns true if path is safe for internal routing
 */
export function isSafeInternalPath(path: string): boolean {
  if (typeof path !== 'string') return false;
  
  // SECURITY: Only allow paths starting with '/' (relative paths)
  if (!path.startsWith('/')) return false;
  
  // SECURITY: Basic length cap to prevent DoS
  if (path.length > 500) return false;
  
  // SECURITY: Disallow CRLF and control characters (prevent header injection)
  // eslint-disable-next-line no-control-regex
  if (/[\r\n\t\u0000]/.test(path)) return false;
  
  // SECURITY: Disallow '//' at start (could be interpreted as scheme-relative URL)
  if (path.startsWith('//')) return false;
  
  // SECURITY: Prevent path traversal attacks
  if (path.includes('..')) return false;
  
  // SECURITY: Prevent URL-encoded path traversal patterns
  if (path.includes('%2e%2e') || path.includes('%2E%2E')) return false; // URL-encoded ..
  if (path.includes('%2f') || path.includes('%2F')) return false; // URL-encoded /
  if (path.includes('%5c') || path.includes('%5C')) return false; // URL-encoded \
  
  // SECURITY: Prevent URL-encoded protocol separators
  if (path.includes('%3a') || path.includes('%3A')) return false; // URL-encoded :
  if (path.includes('%3f') || path.includes('%3F')) return false; // URL-encoded ?
  
  // SECURITY: Prevent email-like patterns (could be used in some attack vectors)
  if (path.includes('@')) return false;
  
  // SECURITY: Ensure path is not empty after validation
  if (path.length === 0 || path === '/') return true; // Allow root path
  
  return true;
}

